import {
  AppMetadataSchema,
  AppSettingsSchema,
  AppSettingsUpdateSchema,
  APP_METADATA_ID,
  BackupMetadataSchema,
  BACKUP_METADATA_ID,
  createDefaultAppMetadata,
  createDefaultBackupMetadata,
  createDefaultSettings,
  SETTINGS_ID,
  type AppMetadata,
  type AppSettings,
  type BackupMetadata,
  type UpdateAppSettingsInput,
} from "../../domain";
import type { DuaWorkspaceDatabase } from "../database";
import { assertLiveReferences, type RepositoryOptions } from "./repository-utils";

function deepMerge<T extends Record<string, unknown>>(base: T, patch: Record<string, unknown>): T {
  const result: Record<string, unknown> = { ...base };
  for (const [key, value] of Object.entries(patch)) {
    const existing = result[key];
    if (
      value !== null &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      existing !== null &&
      typeof existing === "object" &&
      !Array.isArray(existing)
    ) {
      result[key] = deepMerge(existing as Record<string, unknown>, value as Record<string, unknown>);
    } else {
      result[key] = value;
    }
  }
  return result as T;
}

export class SettingsRepository {
  readonly #database: DuaWorkspaceDatabase;
  readonly #now: () => string;

  constructor(database: DuaWorkspaceDatabase, options: RepositoryOptions = {}) {
    this.#database = database;
    this.#now = options.now ?? (() => new Date().toISOString());
  }

  async get(): Promise<AppSettings> {
    const existing = await this.#database.settings.get(SETTINGS_ID);
    if (existing !== undefined) {
      return AppSettingsSchema.parse(existing);
    }

    return this.#database.transaction("rw", this.#database.settings, async () =>
      this.#getOrCreate(),
    );
  }

  async #getOrCreate(): Promise<AppSettings> {
    const existing = await this.#database.settings.get(SETTINGS_ID);
    if (existing !== undefined) {
      return AppSettingsSchema.parse(existing);
    }
    const created = createDefaultSettings(this.#now());
    await this.#database.settings.put(created);
    return created;
  }

  async update(input: UpdateAppSettingsInput): Promise<AppSettings> {
    const patch = AppSettingsUpdateSchema.parse(input);
    return this.#database.transaction(
      "rw",
      [this.#database.settings, this.#database.collections, this.#database.people],
      async () => {
        if (patch.focus?.collectionIds !== undefined) {
          await assertLiveReferences(
            this.#database.collections,
            patch.focus.collectionIds,
            "Collection",
          );
        }
        if (patch.focus?.personIds !== undefined) {
          await assertLiveReferences(this.#database.people, patch.focus.personIds, "Person");
        }
        const current = await this.#getOrCreate();
        const updated = AppSettingsSchema.parse(
          deepMerge(current as unknown as Record<string, unknown>, {
            ...patch,
            updatedAt: this.#now(),
          }),
        );
        await this.#database.settings.put(updated);
        return updated;
      },
    );
  }

  async reset(timezone?: string): Promise<AppSettings> {
    const created = createDefaultSettings(this.#now(), timezone);
    await this.#database.settings.put(created);
    return created;
  }
}

export class MetadataRepository {
  readonly #database: DuaWorkspaceDatabase;
  readonly #now: () => string;

  constructor(database: DuaWorkspaceDatabase, options: RepositoryOptions = {}) {
    this.#database = database;
    this.#now = options.now ?? (() => new Date().toISOString());
  }

  async getAppMetadata(): Promise<AppMetadata> {
    const existing = await this.#database.appMetadata.get(APP_METADATA_ID);
    if (existing !== undefined) {
      return AppMetadataSchema.parse(existing);
    }

    return this.#database.transaction("rw", this.#database.appMetadata, async () =>
      this.#getOrCreateAppMetadata(),
    );
  }

  async #getOrCreateAppMetadata(): Promise<AppMetadata> {
    const existing = await this.#database.appMetadata.get(APP_METADATA_ID);
    if (existing !== undefined) {
      return AppMetadataSchema.parse(existing);
    }
    const created = createDefaultAppMetadata(this.#now());
    await this.#database.appMetadata.put(created);
    return created;
  }

  async updateAppMetadata(
    patch: Partial<Pick<AppMetadata, "bootstrapVersion" | "lastOpenedAt" | "onboardingCompletedAt">>,
  ): Promise<AppMetadata> {
    return this.#database.transaction("rw", this.#database.appMetadata, async () => {
      const current = await this.#getOrCreateAppMetadata();
      const updated = AppMetadataSchema.parse({ ...current, ...patch, updatedAt: this.#now() });
      await this.#database.appMetadata.put(updated);
      return updated;
    });
  }

  async getBackupMetadata(): Promise<BackupMetadata> {
    const existing = await this.#database.backupMetadata.get(BACKUP_METADATA_ID);
    if (existing !== undefined) {
      return BackupMetadataSchema.parse(existing);
    }

    return this.#database.transaction("rw", this.#database.backupMetadata, async () =>
      this.#getOrCreateBackupMetadata(),
    );
  }

  async #getOrCreateBackupMetadata(): Promise<BackupMetadata> {
    const existing = await this.#database.backupMetadata.get(BACKUP_METADATA_ID);
    if (existing !== undefined) {
      return BackupMetadataSchema.parse(existing);
    }
    const created = createDefaultBackupMetadata(this.#now());
    await this.#database.backupMetadata.put(created);
    return created;
  }

  async updateBackupMetadata(
    patch: Partial<
      Pick<
        BackupMetadata,
        | "lastSuccessfulBackupAt"
        | "lastVerifiedBackupAt"
        | "lastBackupEncrypted"
        | "lastBackupCounts"
      >
    >,
  ): Promise<BackupMetadata> {
    return this.#database.transaction("rw", this.#database.backupMetadata, async () => {
      const current = await this.#getOrCreateBackupMetadata();
      const updated = BackupMetadataSchema.parse({ ...current, ...patch, updatedAt: this.#now() });
      await this.#database.backupMetadata.put(updated);
      return updated;
    });
  }
}
